"use client";

import { useCompatRouter } from "@website/hooks/compat-router";
import { CHATBOT_SEARCH_PARAMETER } from "./constants";

type ChatBotRouter = {
  back: () => void;
  isAppRouter: boolean;
  isReady?: boolean;
  pathname: string;
  push: (
    pathname: string,
    query: ChatBotQuery,
    options: ChatBotNavigationOptions
  ) => unknown;
  query: ChatBotQuery;
  replace: (
    pathname: string,
    query: ChatBotQuery,
    options: ChatBotNavigationOptions
  ) => unknown;
};

type ChatBotNavigationOptions = {
  historyState?: object;
  scroll: boolean;
  shallow: boolean;
};

type ChatBotQuery = Record<string, string | Array<string> | undefined>;

const navigationOptions = {
  scroll: false,
  shallow: true
};

const getQueryWithoutChatbot = (query: ChatBotQuery) =>
  Object.fromEntries(
    Object.entries(query).filter(([key]) => key !== CHATBOT_SEARCH_PARAMETER)
  );

export const isChatBotOpen = (router: ChatBotRouter) =>
  router.isReady !== false && CHATBOT_SEARCH_PARAMETER in router.query;

export const openChatBot = (router: ChatBotRouter) =>
  router.push(
    router.pathname,
    {
      ...router.query,
      [CHATBOT_SEARCH_PARAMETER]: "true"
    },
    {
      ...navigationOptions,
      historyState: { modalEntry: true }
    }
  );

export const closeChatBot = (router: ChatBotRouter) => {
  if (!router.isAppRouter || window.history.state?.modalEntry) {
    router.back();
    return;
  }

  return router.replace(
    router.pathname,
    getQueryWithoutChatbot(router.query),
    navigationOptions
  );
};

export const toggleChatBot = (router: ChatBotRouter) =>
  isChatBotOpen(router) ? closeChatBot(router) : openChatBot(router);

export const useChatBotNavigation = () => {
  const router = useCompatRouter();

  return {
    close: () => closeChatBot(router),
    isOpen: isChatBotOpen(router),
    open: () => openChatBot(router),
    toggle: () => toggleChatBot(router)
  };
};
